/**
 * Shared feature-flag tree (mirrors policyUtils SCOPED_FEATURES_DATA).
 * Used by FeaturesPage (customer) and TenantFeatureFlagsSection (tenant overrides).
 */

export interface TreeNode {
  service: string
  displayName: string
  children?: TreeNode[]
}

export interface TreeRoot {
  prefix: string
  service: string
  displayName: string
  children?: TreeNode[]
  /**
   * Exact deployed flag key when this root is not a `{prefix}_view` tree flag
   * (e.g. `aqua_mcp_server`). Without this, RootBlock looks for `{prefix}_view`
   * and the flag is silently excluded from the Features UI.
   */
  flagKey?: string
}

export interface FeatureFlagSidebarSection {
  id: string
  label: string
  roots: TreeRoot[]
  /** Tenant-only: section cannot be toggled */
  locked?: boolean
}

/** Map sidebar section id → flag.category values from FEATURE_FLAG_META */
export const SECTION_FLAG_CATEGORIES: Record<string, string[]> = {
  core: ['core'],
  blueprints: ['ai_blueprints'],
  agent_development: ['agent_development'],
  observability: ['observability'],
  agent_ops: ['agent_ops'],
  interaction: ['interaction'],
  administration: ['administration'],
  platform_admin: ['platform_admin'],
}

export const FEATURE_FLAG_SECTIONS: FeatureFlagSidebarSection[] = [
  {
    id: 'core',
    label: 'Core',
    roots: [
      {
        prefix: 'home',
        service: 'home-feature-access',
        displayName: 'Home',
        children: [
          { service: 'overview', displayName: 'Overview' },
          { service: 'agents', displayName: 'Agents' },
          { service: 'workflows', displayName: 'Workflows' },
          { service: 'evals', displayName: 'Evals' },
          { service: 'sessions', displayName: 'Sessions' },
          { service: 'cost', displayName: 'Cost' },
        ],
      },
      { prefix: 'solutions', service: 'solutions-feature-access', displayName: 'Solutions' },
      { prefix: 'agent_store', service: 'agent-store-feature-access', displayName: 'Agent Store' },
      {
        prefix: 'workflow_store',
        service: 'workflow-store-feature-access',
        displayName: 'Workflow Store',
      },
    ],
  },
  {
    id: 'blueprints',
    label: 'Blueprints',
    roots: [
      {
        prefix: 'ai_blueprints',
        service: 'ai-blueprints-feature-access',
        displayName: 'Blueprints',
      },
      { prefix: 'ideate', service: 'ideate-feature-access', displayName: 'Ideate' },
    ],
  },
  {
    id: 'enterprise_ontology',
    label: 'Enterprise Ontology',
    roots: [
      {
        prefix: 'ontology',
        service: 'ontology-feature-access',
        displayName: 'Ontology',
        children: [
          { service: 'canvas', displayName: 'Canvas' },
          { service: 'onto_eval', displayName: 'Onto Eval' },
          { service: 'data_models', displayName: 'Data Models' },
          { service: 'kpi_library', displayName: 'KPI Library' },
          { service: 'semantic_hub', displayName: 'Semantic Hub' },
          { service: 'migrations', displayName: 'Migrations' },
          { service: 'build_with_ai', displayName: 'Build with AI' },
        ],
      },
    ],
  },
  {
    id: 'agent_development',
    label: 'Agent Development',
    roots: [
      {
        prefix: 'projects',
        service: 'projects-feature-access',
        displayName: 'Projects',
        children: [
          { service: 'setup_wizard', displayName: 'Setup Wizard' },
          {
            service: 'configure',
            displayName: 'Configure',
            children: [
              { service: 'members', displayName: 'Members' },
              { service: 'orchestrators', displayName: 'Orchestrators' },
              { service: 'connections', displayName: 'Connections' },
              { service: 'identities', displayName: 'Agent identities' },
              { service: 'prompts', displayName: 'Prompts' },
              { service: 'models', displayName: 'Models' },
              { service: 'tools', displayName: 'Tools' },
              { service: 'skills', displayName: 'Skills' },
              { service: 'knowledge', displayName: 'Knowledge' },
              { service: 'graph', displayName: 'Graph' },
              { service: 'vector', displayName: 'Vector' },
              { service: 'storage', displayName: 'Storage' },
            ],
          },
          {
            service: 'details',
            displayName: 'Details',
            children: [
              { service: 'overview', displayName: 'Overview' },
              { service: 'agents', displayName: 'Agents' },
              { service: 'workflows', displayName: 'Workflows' },
              { service: 'registry', displayName: 'Registry' },
              { service: 'evals', displayName: 'Evals' },
              { service: 'deployments', displayName: 'Deployments' },
            ],
          },
        ],
      },
      {
        prefix: 'data',
        service: 'data-feature-access',
        displayName: 'Data',
        children: [
          { service: 'knowledge', displayName: 'Knowledge' },
          { service: 'graph', displayName: 'Graph' },
          { service: 'vector_db', displayName: 'Vector DB' },
          { service: 'storage', displayName: 'Storage' },
        ],
      },
      {
        prefix: 'prompts',
        service: 'prompts-feature-access',
        displayName: 'Prompts',
        children: [{ service: 'prompt_optimization', displayName: 'Optimize Prompts' }],
      },
      { prefix: 'skills', service: 'skills-feature-access', displayName: 'Skills' },
      {
        prefix: 'agent_studio',
        service: 'agent-studio-feature-access',
        displayName: 'Agent Studio',
        children: [
          { service: 'auto_tune', displayName: 'Auto Tune' },
          { service: 'build_with_ai', displayName: 'Build with AI' },
        ],
      },
      {
        prefix: 'agent_workflows',
        service: 'agent-workflows-feature-access',
        displayName: 'Agent Workflows',
        children: [
          { service: 'workflows', displayName: 'Workflows' },
          { service: 'solution_templates', displayName: 'Solution Templates' },
          { service: 'build_with_ai', displayName: 'Build with AI' },
          { service: 'metrics', displayName: 'Metrics' },
        ],
      },
    ],
  },
  {
    id: 'observability',
    label: 'Observability',
    roots: [
      {
        prefix: 'agent_registry',
        service: 'registry-feature-access',
        displayName: 'Agent Registry',
        children: [
          { service: 'agents', displayName: 'Agents' },
          { service: 'workflows', displayName: 'Workflows' },
          {
            service: 'guardrails',
            displayName: 'Guardrails',
            children: [
              { service: 'guardrails_repository', displayName: 'Guardrails Repository' },
              { service: 'agent_guardrails', displayName: 'Agent Guardrails' },
            ],
          },
        ],
      },
      {
        // Standalone root (no RBAC sub-feature children) — mirrors Agent Registry data
        // via GET /agents/registry. There is deliberately no `ai-transparency-feature-access`
        // policy document or role-editor row: the nav item and App.tsx route reuse the
        // `agent-registry` grant (see AI Transparency in navigation.ts). Pointing this at a
        // service that does not exist made the flag look role-managed while its grants were
        // owned by Agent Registry, so it now names the service that actually enforces it.
        prefix: 'ai_transparency',
        service: 'registry-feature-access',
        displayName: 'AI Transparency',
      },
      {
        prefix: 'evals_simulations',
        service: 'evals-simulations-feature-access',
        displayName: 'Evals & Simulations',
        children: [
          // Leaderboards / Simulations / Environments are hidden (flags off) — not listed.
          { service: 'benchmarks', displayName: 'Benchmarks' },
          {
            service: 'evaluations',
            displayName: 'Evaluations',
            children: [
              { service: 'eval_runs', displayName: 'Eval Runs' },
              { service: 'agent_evals_templates', displayName: 'Eval Templates' },
            ],
          },
        ],
      },
      {
        prefix: 'monitoring',
        service: 'monitoring-feature-access',
        displayName: 'Monitoring',
        children: [
          {
            service: 'deployments',
            displayName: 'Deployments',
            children: [
              { service: 'dep_agents', displayName: 'Agents' },
              { service: 'dep_workflows', displayName: 'Workflows' },
            ],
          },
          { service: 'hitl_inbox', displayName: 'HITL Inbox' },
          { service: 'rag_analytics', displayName: 'RAG Analytics' },
          {
            service: 'token_usage',
            displayName: 'Token Usage',
            children: [
              { service: 'consumption', displayName: 'Consumption' },
              { service: 'llm_usage', displayName: 'LLM Usage' },
            ],
          },
          { service: 'cache', displayName: 'Cache' },
          { service: 'guardrails', displayName: 'Guardrails' },
          { service: 'voice', displayName: 'Voice' },
          { service: 'policies', displayName: 'Policies' },
          { service: 'alerts', displayName: 'Alerts' },
          { service: 'jobs', displayName: 'Monitoring Jobs' },
          { service: 'performance', displayName: 'Quality & Evals' },
        ],
      },
    ],
  },
  {
    id: 'agent_ops',
    label: 'Agent Ops',
    roots: [
      {
        prefix: 'models',
        service: 'models-feature-access',
        displayName: 'Models',
        children: [
          { service: 'model_arena', displayName: 'Model Arena' },
          { service: 'pricing_catalog', displayName: 'Pricing Catalog' },
        ],
      },
      { prefix: 'tools', service: 'tools-feature-access', displayName: 'Tools' },
      {
        prefix: 'agent_identities',
        service: 'agent-identities-feature-access',
        displayName: 'Agent identities',
      },
      {
        prefix: 'connections',
        service: 'connectors-feature-access',
        displayName: 'Connections',
        children: [
          { service: 'databases', displayName: 'Databases' },
          { service: 'storage_accounts', displayName: 'Storage Accounts' },
          { service: 'orchestrators', displayName: 'Orchestrators' },
          { service: 'telephony', displayName: 'Telephony' },
          { service: 'service_connections', displayName: 'Service Connections' },
        ],
      },
      // Standalone META flags (not `{prefix}_view`). Without `flagKey`, the Features
      // page would look for `aqua_mcp_server_view` / match no agent_ops prefix and
      // silently omit the deployed toggle.
      {
        prefix: 'aqua_mcp_server',
        flagKey: 'aqua_mcp_server',
        service: 'aqua-mcp-server-feature-access',
        displayName: 'MCP Server (Cowork)',
      },
      {
        prefix: 'aqua_mcp_update_agent_graph',
        flagKey: 'aqua_mcp_update_agent_graph',
        service: 'aqua-mcp-update-agent-graph-feature-access',
        displayName: 'MCP Update Agent Graph',
      },
    ],
  },
  {
    // Sidebar RESOURCES group. Docs used to sit under Core, which no longer matched
    // where the user finds it in the nav.
    id: 'resources',
    label: 'Resources',
    roots: [{ prefix: 'docs', service: 'docs-feature-access', displayName: 'Docs' }],
  },
  {
    id: 'voice_agents',
    label: 'Voice Agents',
    roots: [
      {
        prefix: 'voice_agents',
        service: 'voice-agents-feature-access',
        displayName: 'Voice Agents',
        children: [
          { service: 'manage_numbers', displayName: 'Manage Numbers' },
          { service: 'manage_calls', displayName: 'Manage Calls' },
          { service: 'manage_recordings', displayName: 'Manage Recordings' },
          { service: 'analytics', displayName: 'Analytics' },
          { service: 'canvas', displayName: 'Canvas' },
        ],
      },
    ],
  },
]

/** Tenant UI: extra sidebar sections (flat list, no tree roots) */
export const TENANT_EXTRA_FEATURE_FLAG_SECTIONS: FeatureFlagSidebarSection[] = [
  { id: 'interaction', label: 'Interaction', roots: [] },
  { id: 'administration', label: 'Administration', roots: [] },
  // Platform Admin flags must never be disabled — doing so locks admins out of this
  // very page. Section-locked here; flags are also guarded per-key.
  { id: 'platform_admin', label: 'Platform Admin', roots: [], locked: true },
]

export const TENANT_FEATURE_FLAG_SECTIONS: FeatureFlagSidebarSection[] = [
  ...FEATURE_FLAG_SECTIONS,
  ...TENANT_EXTRA_FEATURE_FLAG_SECTIONS,
]

export function flagKeyForPath(prefix: string, pathSegments: string[]): string {
  return [prefix, ...pathSegments, 'view'].join('_')
}

/** Resolved flag key for a tree root (`flagKey` override or `{prefix}_view`). */
export function rootFlagKey(root: TreeRoot): string {
  return root.flagKey ?? flagKeyForPath(root.prefix, [])
}

/**
 * Flag key for a node under a root. At the root path, honors `rootExactKey`
 * (TreeRoot.flagKey) so non-`*_view` META flags still resolve.
 */
export function resolveTreeFlagKey(
  prefix: string,
  pathSegments: string[],
  rootExactKey?: string,
): string {
  if (pathSegments.length === 0 && rootExactKey) return rootExactKey
  return flagKeyForPath(prefix, pathSegments)
}

/**
 * Flags that must stay permanently enabled and can never be toggled off from the UI.
 * Disabling a `platform_admin_*` flag would lock admins out of the Features/Tenants
 * admin pages themselves (no way back), so their toggles render locked and the UI never
 * sends `enabled: false` for them.
 *
 * `admin_*` is locked for the same reason: the Admin and Platform Admin sections are
 * hidden from the flag UIs (see featureFlagsNavMirror) because those pages are gated by
 * role, not by tenant configuration. With no section to flip them back from, the global
 * "disable all" toggle would otherwise strand `admin_view` off.
 *
 * Docs is NOT locked: `docs_view` gates only the nav item and the /resources/docs route,
 * so it stays admin-toggleable like any other feature.
 */
export function isPermanentlyEnabledFlag(flagKey: string): boolean {
  if (ALWAYS_ON_EXCEPTIONS.has(flagKey)) return false
  if (ALWAYS_ON_FLAG_KEYS.has(flagKey)) return true
  return ALWAYS_ON_FLAG_PREFIXES.some((prefix) => flagKey.startsWith(prefix))
}

/** Mirrors the backend always-on mask in `app/data/feature_flag_template_mask.py` — keep
 *  the two in step, or a template apply will switch off what this hides. */
const ALWAYS_ON_FLAG_KEYS = new Set(['projects_setup_wizard_view'])
const ALWAYS_ON_FLAG_PREFIXES = [
  'platform_admin_',
  'admin_',
  'projects_details_',
  'projects_configure_',
]
/** `projects_configure_view` is the one Projects row that stays tenant-managed, so it is
 *  carved back out of the `projects_configure_` prefix above. */
const ALWAYS_ON_EXCEPTIONS = new Set(['projects_configure_view'])

export function collectKeys(
  prefix: string,
  node: TreeNode | TreeRoot,
  pathSoFar: string[],
  rootExactKey?: string,
): string[] {
  const out = [resolveTreeFlagKey(prefix, pathSoFar, rootExactKey)]
  for (const child of node.children ?? []) {
    out.push(...collectKeys(prefix, child, [...pathSoFar, child.service], rootExactKey))
  }
  return out
}

export function collectKeysForRoot(root: TreeRoot): string[] {
  return collectKeys(root.prefix, root, [], root.flagKey)
}

export function collectSectionTreeKeys(section: FeatureFlagSidebarSection): string[] {
  return section.roots.flatMap((root) => collectKeysForRoot(root))
}

/**
 * Whether a flag belongs to a given sidebar section.
 * Tree sections (with roots): match by flag key prefix (e.g. ontology_ → Enterprise Ontology)
 * or an exact TreeRoot.flagKey (non-`*_view` META flags).
 * Flat sections (Administration, Platform Admin): match by backend category.
 *
 * Takes the section itself so a caller rendering a filtered section list (see
 * featureFlagsNavMirror) matches against the roots it actually shows, instead of the
 * full registry — otherwise a hidden root's flags still count toward that section.
 */
export function flagMatchesSection(
  flagKey: string,
  flagCategory: string | undefined,
  section: FeatureFlagSidebarSection,
): boolean {
  if (section.roots.length > 0) {
    return section.roots.some(
      (root) => flagKey === rootFlagKey(root) || flagKey.startsWith(`${root.prefix}_`),
    )
  }

  const categories = SECTION_FLAG_CATEGORIES[section.id]
  if (!categories) return false
  return categories.includes(flagCategory || 'core')
}

/** `flagMatchesSection` against the full registry, by section id. */
export function flagBelongsToSection(
  flagKey: string,
  flagCategory: string | undefined,
  sectionId: string,
): boolean {
  const section =
    FEATURE_FLAG_SECTIONS.find((s) => s.id === sectionId) ??
    TENANT_EXTRA_FEATURE_FLAG_SECTIONS.find((s) => s.id === sectionId)
  if (!section) return false
  return flagMatchesSection(flagKey, flagCategory, section)
}

export function nodeMatchesSearch(
  prefix: string,
  node: TreeNode | TreeRoot,
  path: string[],
  query: string,
  rootExactKey?: string,
): boolean {
  const q = query.toLowerCase()
  const key = resolveTreeFlagKey(prefix, path, rootExactKey)
  if (node.displayName.toLowerCase().includes(q) || key.toLowerCase().includes(q)) return true
  return (node.children ?? []).some((c) =>
    nodeMatchesSearch(prefix, c, [...path, c.service], query, rootExactKey),
  )
}

export function rootHasPresentFlags(
  root: TreeRoot,
  flagsByKey: Record<string, unknown>,
): boolean {
  return collectKeysForRoot(root).some((k) => flagsByKey[k] != null)
}

/** Parent `*_view` flag for each tree view flag (used for cascade + role gating). */
const VIEW_FLAG_PARENT_MAP: Record<string, string | null> = (() => {
  const parent: Record<string, string | null> = {}

  function walk(
    prefix: string,
    node: TreeNode | TreeRoot,
    pathSoFar: string[],
    parentKey: string | null,
    rootExactKey?: string,
  ) {
    const key = resolveTreeFlagKey(prefix, pathSoFar, rootExactKey)
    parent[key] = parentKey
    for (const child of node.children ?? []) {
      walk(prefix, child, [...pathSoFar, child.service], key, rootExactKey)
    }
  }

  for (const section of FEATURE_FLAG_SECTIONS) {
    for (const root of section.roots) {
      walk(root.prefix, root, [], null, root.flagKey)
    }
  }
  return parent
})()

/** All `*_view` keys declared in the feature-flag tree. */
export function getAllTreeViewFlagKeys(): string[] {
  return Object.keys(VIEW_FLAG_PARENT_MAP)
}

/** True when `flagKey` is a nested tree `{prefix}_{path}_view` (not FEATURE_FLAG_META). */
export function isTreeViewFlagKey(flagKey: string): boolean {
  return flagKey in VIEW_FLAG_PARENT_MAP
}

/**
 * Walk ancestors for tree view flags. Fail-closed: a key missing from the
 * payload is not deployed (master YAML disabled) and is treated as disabled.
 */
export function isViewFlagEnabledWithAncestors(
  flagKey: string,
  flags: Record<string, boolean | undefined>,
): boolean {
  if (!(flagKey in VIEW_FLAG_PARENT_MAP)) {
    return flags[flagKey] === true
  }

  let current: string | null = flagKey
  while (current) {
    if (!(current in flags) || flags[current] === false) return false
    current = VIEW_FLAG_PARENT_MAP[current] ?? null
  }
  return true
}

function findPathToService(
  node: TreeNode,
  service: string,
  pathSoFar: string[],
): string[] | null {
  const nextPath = [...pathSoFar, node.service]
  if (node.service === service) return nextPath
  for (const child of node.children ?? []) {
    const found = findPathToService(child, service, nextPath)
    if (found) return found
  }
  return null
}

/**
 * Map scoped RBAC sub-feature service (e.g. `setup_wizard`) to tree view flag key
 * (`projects_setup_wizard_view`). Returns null when not in the tree.
 */
export function resolveTreeViewFlagKey(
  featureKey: string,
  subFeatureService?: string,
): string | null {
  const prefix = featureKey.replace(/-/g, '_')
  if (!subFeatureService) {
    const rootKey = flagKeyForPath(prefix, [])
    return rootKey in VIEW_FLAG_PARENT_MAP ? rootKey : null
  }

  const service = subFeatureService.replace(/-/g, '_')
  for (const section of FEATURE_FLAG_SECTIONS) {
    for (const root of section.roots) {
      if (root.prefix !== prefix) continue
      for (const child of root.children ?? []) {
        const path = findPathToService(child, service, [])
        if (path) return flagKeyForPath(prefix, path)
      }
    }
  }
  return null
}
